import React, { useEffect, useState } from "react";
import CustomModal from "../../../components/generic/custommodal/CustomModal";
import { notification, Spin } from "antd";
import SecondaryButton from "../../../components/generic/buttons/SecondaryButton";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import ErrorNotificationComponent from "../../../components/generic/error/ErrorNotificationComponent";
import StrapiService from "../../articles/StrapiService";
import RichSnippetWriter from "../../articles/RichSnippetWriter";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import { TextField } from "@mui/material";
import SelectPatientName from "../../carecoordinator/requestforvolunteeringservice/components/SelectPatientName";
import { executeIntent } from "../../utils/IntentExecutor";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../utils/configurationconstants";
import moment from "moment";
import IntentType from "../../../intentexecutor/utils/IntentType";
import { getFullName } from "../visits/utils";
import { refreshListArticle } from "../../../service/memory/recoil/listarticle";

const EditArticle = ({ articleId, type, modalOpen, setModalOpen }) => {
  const strapiService = new StrapiService();
  const organizationIdentifier = useGetOrgIdentifier();
  const [buttonLoading, setButtonLoading] = useState(false);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isUpdateDisabled, setIsUpdateDisabled] = useState(true);
  const [topic, setTopic] = useState("");
  const [receivedStrapiData, setReceivedStrapiData] = useState({});
  const [patientName, setPatientName] = useState([]);
  const setRefreshArticle = useSetRecoilState(refreshListArticle);
  const configuration = useRecoilValue(currentConfiguration);
  const secretKey =
    configuration[ConfigurationConstants.NIRAMAYA_ORGANIZATION_SECRETKEY];

  const handleUpdate = async () => {
    setButtonLoading(true);
    try {
      const result = await strapiService.updateBlog(
        articleId,
        receivedStrapiData
      );
      if (result.status === 200) {
        notification.success({
          message: `${type} updated successfully!`,
          placement: "bottomRight",
        });
        setRefreshArticle((preValue) => !preValue);
      } else {
        notification.error({
          message: `Could not update ${type}`,
          placement: "bottomRight",
        });
      }
    } catch (error) {
      ErrorNotificationComponent(error);
    } finally {
      setButtonLoading(false);
      setModalOpen(false);
    }
  };

  const modalButtons = (
    <>
      <SecondaryButton
        buttonText="Cancel"
        handleOnClick={() => setModalOpen(false)}
      />
      <PrimaryButton
        buttonText="Update"
        loading={buttonLoading}
        handleOnClick={handleUpdate}
        buttonDisable={isUpdateDisabled}
      />
    </>
  );

  const handleOnChange = (e) => {
    setTopic(e.target.value);
    setReceivedStrapiData((prevData) => ({
      ...prevData,
      topic: e.target.value,
    }));
    const isContentEmpty = receivedStrapiData?.body?.trim() === "";
    setIsUpdateDisabled(e.target.value === "" || isContentEmpty);
  };

  const handleStrapiDataChange = (data) => {
    const modifiedData = {
      ...data,
      category: type,
      organizationIdentifier,
      topic,
    };
    setReceivedStrapiData(modifiedData);
    const isInputEmpty = topic.trim() === "";
    if (data.body === "<p><br></p>") {
      setIsUpdateDisabled(true);
    } else {
      setIsUpdateDisabled(isInputEmpty || !data?.body?.trim());
    }
  };

  useEffect(() => {
    const getJournal = async () => {
      setLoading(true);
      setModalOpen(true);
      const result = await strapiService.getBlog(articleId);
      setArticle(result?.data);
      setTopic(result?.data?.attributes?.topic);
      setLoading(false);
    };

    if (articleId) {
      getJournal();
    }
  }, [articleId, setModalOpen]);

  useEffect(() => {
    const patient = patientName?.value && JSON.parse(patientName?.value);
    setReceivedStrapiData({
      ...receivedStrapiData,
      patientIdentifier: patient?.userIdentifier,
    });
  }, [patientName]);

  useEffect(() => {
    const getPatientDetails = async () => {
      const payload = {
        userIdentifier: article?.attributes?.patientIdentifier,
        secretKey: secretKey?.key,
      };
      const userData = await executeIntent(
        IntentType.GET_PATIENT_DETAILS_BY_USER_IDENTIFIER,
        payload
      );
      setPatientName({
        label: getFullName(userData[0]?.givenName, userData[0]?.familyName),
        value: JSON.stringify(userData[0]),
        misc: userData[0]?.birthDate
          ? `MRN: ${userData[0]?.medicalRecordNumber} | DOB: ${moment(
              userData[0]?.birthDate
            )?.format("MMM D, YYYY")}`
          : `MRN: ${userData[0]?.medicalRecordNumber}`,
      });
    };

    if (secretKey?.key && article?.attributes?.patientIdentifier) {
      getPatientDetails();
    }
  }, [article?.attributes?.patientIdentifier, secretKey]);

  return (
    <>
      <CustomModal
        title={`Update ${type}`}
        isModalOpen={modalOpen}
        closeModal={setModalOpen}
        footer={[modalButtons]}
      >
        {loading ? (
          <Spin
            size="medium"
            className="d-flex justify-content-center m-auto"
          />
        ) : (
          <>
            <div className="journal-header-section">
              <label htmlFor="topicInput" className="required-mark-label">
                Topic
              </label>
              <TextField
                fullWidth
                label={`${type} Topic`}
                type="text"
                id="topicInput"
                value={topic}
                onChange={handleOnChange}
              />
            </div>
            <div className="journal-richsnippet-section">
              <RichSnippetWriter
                key={0}
                onStrapiDataChange={handleStrapiDataChange}
                content={article?.attributes?.body}
              />
            </div>
            <div className="journal-patient-selector-section">
              {article?.attributes?.patientIdentifier && (
                <SelectPatientName
                  patientName={patientName}
                  setPatientName={setPatientName}
                  showCancel={true}
                />
              )}
            </div>
          </>
        )}
      </CustomModal>
    </>
  );
};

export default EditArticle;
